import { handleError } from '@services/errHandler';
import { PermissionsService } from '@services/permissions';
import { permissions, ResourceByOp, ResourceType } from '@vincerosoft/assessfluence-types';

type ReadOperation = 'read-own' | 'read-group' | 'read-all' | 'read';

export type RouteGuardInput = {
  requiresAuth?: boolean;
  isAuthenticated: boolean;
  resourceId?: ResourceType;
  redirectUnauthorizedTo?: string;
  redirectUnauthenticatedTo?: string;
};

export type RouteGuardDecision = {
  canAccess: boolean;
  redirectTo?: string;
};

function resolveReadOperation(resourceId: ResourceType): ReadOperation | null {
  const operationsOnResource = permissions.filter((perm) => perm.resource === resourceId);

  if (operationsOnResource.length === 0) {
    return null;
  }

  const readOperation = operationsOnResource.find((perm) => perm.operation.includes('read'))
    ?.operation as ReadOperation | undefined;

  if (!readOperation) {
    return null;
  }

  return readOperation;
}

function canReadResource(resourceId: ResourceType, operation: ReadOperation): boolean {
  if (operation === 'read-own') {
    return PermissionsService.canReadOwn(resourceId as ResourceByOp['read-own'][number]);
  }

  if (operation === 'read-group') {
    return PermissionsService.canReadGroup(resourceId as ResourceByOp['read-group'][number]);
  }

  if (operation === 'read-all') {
    return PermissionsService.canReadAll(resourceId as ResourceByOp['read-all'][number]);
  }

  return PermissionsService.canRead(resourceId as ResourceByOp['read'][number]);
}

export function canActivateRoute({
  requiresAuth,
  isAuthenticated,
  resourceId,
  redirectUnauthorizedTo = '/',
  redirectUnauthenticatedTo = '/__/auth'
}: RouteGuardInput): RouteGuardDecision {
  if (!requiresAuth) {
    return { canAccess: true };
  }

  if (!isAuthenticated) {
    return { canAccess: false, redirectTo: redirectUnauthenticatedTo };
  }

  if (!resourceId) {
    return { canAccess: true };
  }

  const operation = resolveReadOperation(resourceId);

  if (!operation) {
    return { canAccess: false, redirectTo: redirectUnauthorizedTo };
  }

  const hasAccess = canReadResource(resourceId, operation);

  if (!hasAccess) {
    handleError(new Error('Unauthorized access attempt'), 'Error in AuthGuard');
    return { canAccess: false, redirectTo: redirectUnauthorizedTo };
  }

  return { canAccess: true };
}
